import { useId, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { CampoDoMotivo } from '@/componentes/CampoDoMotivo';
import { Janela } from '@/componentes/JanelaDeAcao';
import { EXEMPLO_DO_MOTIVO, faltaNoMotivo } from '@/lib/motivo';

import { MensagemDeErro } from './Avisos';
import { useCadastro, useDuplicarPapel, useFixarFornecedorDoPapel } from './consultas';
import { fornecedorEscrito, nomeDaCopia } from './papel-de-parceiro';
import type { Papel } from './tipos';

/**
 * 0132 · DUPLICAR O PAPEL (decisão 58), na mesa.
 *
 * O dono (30/10/2026): "crie uma funcionalidade de duplicar os papéis para que
 * eu possa modificar e facilitar a parametrização dos próximos postos que for
 * cadastrar, assim como outros colaboradores". A cópia leva as permissões, a
 * trava e o início do app; no papel de posto parceiro, a janela pede o
 * fornecedor da cópia (o próximo posto) ou deixa para escolher na concessão.
 *
 * Janela, e não gaveta: o painel do papel fecha enquanto ela está aberta (R01
 * proíbe gaveta e janela empilhadas), e reabre já no papel novo.
 */
export function JanelaDeDuplicar({
  papel,
  aberta,
  aoFechar,
  aoDuplicado,
}: {
  readonly papel: Papel;
  readonly aberta: boolean;
  readonly aoFechar: () => void;
  readonly aoDuplicado: (novo: Papel) => void;
}) {
  const formulario = useId();
  const duplicar = useDuplicarPapel(papel);
  const parceiro = papel.parceiro === true;
  const fornecedores = useCadastro('fornecedores', aberta && parceiro);
  const [nome, setNome] = useState(() => nomeDaCopia(papel.nome));
  const [fornecedor, setFornecedor] = useState(papel.fornecedor_id ?? '');
  const [motivo, setMotivo] = useState('');
  const [tentou, setTentou] = useState(false);
  const faltaNome = nome.trim() === '' ? 'Escreva o nome do novo papel.' : null;
  const faltaMotivo = faltaNoMotivo(motivo);

  return (
    <Janela
      titulo={`Duplicar o papel ${papel.nome}`}
      descricao="A cópia leva as permissões, a trava do lançamento e o início do app. O código nasce do nome."
      aberta={aberta}
      aoFechar={aoFechar}
      acoes={
        <>
          <Botao nivel="secundario" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao nivel="primario" type="submit" form={formulario} disabled={duplicar.isPending}>
            {duplicar.isPending ? 'Duplicando…' : 'Duplicar papel'}
          </Botao>
        </>
      }
    >
      <form
        id={formulario}
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          setTentou(true);
          if (faltaNome || faltaMotivo) return;
          duplicar.mutate(
            { nome: nome.trim(), ...(parceiro ? { fornecedorId: fornecedor || null } : {}), motivo: motivo.trim() },
            { onSuccess: aoDuplicado },
          );
        }}
      >
        <MensagemDeErro erro={duplicar.error} />
        <CampoTexto rotulo="Nome do novo papel" valor={nome} aoMudar={setNome} ajuda="O nome que a lista de papéis e a concessão mostram." />
        {tentou && faltaNome ? (
          <p role="alert" className="txt-apoio text-erro-texto">
            {faltaNome}
          </p>
        ) : null}
        {parceiro ? (
          <CampoSelecao
            rotulo="Fornecedor"
            valor={fornecedor}
            aoMudar={setFornecedor}
            rotuloVazio="Escolher na concessão"
            opcoes={(fornecedores.data ?? [])
              .filter((f) => f.ativo || f.id === fornecedor)
              .map((f) => ({ valor: f.id, rotulo: `${f.nome ?? f.codigo} · ${f.codigo}` }))}
            ajuda="O posto da cópia. Com ele escolhido, quem receber o papel já recebe este fornecedor; sem ele, o fornecedor é escolhido em cada concessão."
            {...(fornecedores.isError ? { erro: 'Não foi possível carregar os fornecedores. Feche a janela e abra de novo.' } : {})}
          />
        ) : null}
        <CampoDoMotivo valor={motivo} aoMudar={setMotivo} exemplo={`Por exemplo: ${EXEMPLO_DO_MOTIVO['papel.duplicar']}`} />
        {tentou && faltaMotivo ? (
          <p role="alert" className="txt-apoio text-erro-texto">
            {faltaMotivo}
          </p>
        ) : null}
      </form>
    </Janela>
  );
}

/**
 * 0132 · O FORNECEDOR DO PAPEL DE PARCEIRO, na aba Trava. A trava em si não se
 * edita no parceiro (só externo, no posto do fornecedor); o que se escolhe é
 * se o papel é de UM posto ou se o fornecedor fica para a concessão.
 */
export function FornecedorDoPapel({ papel, gere }: { readonly papel: Papel; readonly gere: boolean }) {
  const gravar = useFixarFornecedorDoPapel(papel);
  const fornecedores = useCadastro('fornecedores', gere);
  const [fornecedor, setFornecedor] = useState(papel.fornecedor_id ?? '');
  const [motivo, setMotivo] = useState('');
  const [tentou, setTentou] = useState(false);
  const faltaMotivo = faltaNoMotivo(motivo);
  const semMudanca = (papel.fornecedor_id ?? '') === fornecedor;
  const nomeDe = (id: string) => {
    const f = (fornecedores.data ?? []).find((k) => k.id === id);
    return f ? (f.nome ?? f.codigo) : 'Escolhido na concessão';
  };

  return (
    <div className="flex flex-col gap-3 border-t border-borda-sutil pt-4">
      <p className="txt-corpo text-tinta">
        <span className="txt-rotulo-campo">Fornecedor do papel: </span>
        {fornecedorEscrito(papel)}
      </p>
      {gere ? (
        <form
          className="flex flex-col gap-3"
          noValidate
          onSubmit={(evento) => {
            evento.preventDefault();
            setTentou(true);
            if (faltaMotivo || semMudanca) return;
            gravar.mutate(
              { fornecedorId: fornecedor || null, versao: papel.versao, motivo: motivo.trim(), nome: nomeDe(fornecedor) },
              {
                onSuccess: () => {
                  setMotivo('');
                  setTentou(false);
                },
              },
            );
          }}
        >
          <MensagemDeErro erro={gravar.error} />
          <CampoSelecao
            rotulo="Fornecedor"
            valor={fornecedor}
            aoMudar={setFornecedor}
            rotuloVazio="Escolher na concessão"
            opcoes={(fornecedores.data ?? [])
              .filter((f) => f.ativo || f.id === fornecedor)
              .map((f) => ({ valor: f.id, rotulo: `${f.nome ?? f.codigo} · ${f.codigo}` }))}
            ajuda="Com o fornecedor escolhido, o papel é de UM posto: quem o recebe já recebe este fornecedor."
          />
          <CampoDoMotivo valor={motivo} aoMudar={setMotivo} exemplo={`Por exemplo: ${EXEMPLO_DO_MOTIVO['papel.fixar_fornecedor']}`} />
          {tentou && faltaMotivo ? (
            <p role="alert" className="txt-apoio text-erro-texto">
              {faltaMotivo}
            </p>
          ) : null}
          {tentou && semMudanca && !faltaMotivo ? (
            <p role="alert" className="txt-apoio text-erro-texto">
              O fornecedor escolhido é o de hoje. Escolha outro para gravar.
            </p>
          ) : null}
          <div className="flex justify-end">
            <Botao nivel="secundario" type="submit" disabled={gravar.isPending}>
              {gravar.isPending ? 'Gravando o fornecedor…' : 'Salvar fornecedor'}
            </Botao>
          </div>
        </form>
      ) : null}
    </div>
  );
}
